import React, { createContext, useContext, useMemo } from 'react'

import useAppContext from 'hooks/useAppContext'
import usePersistedState from 'hooks/usePersistedState'
import { accountType } from 'models/Account'
import {
  DATE_RANGE_LABELS,
  RETIRED_TODAY_LABEL,
  buildOlderThan7DaysRange,
  type ActivationDate
} from 'components/dashboardCalendar/dateRanges'
import type { DashboardFilterState } from 'components/dashboardFilters/types'

const STORAGE_KEY_STAFF = 'dashboard_filters_staff'
const STORAGE_KEY_PROVIDER = 'dashboard_filters_provider'

function hydrateFilters(filters: DashboardFilterState): DashboardFilterState {
  if (!filters.dateRange) return filters
  return {
    ...filters,
    dateRange: {
      from: filters.dateRange.from
        ? new Date(filters.dateRange.from as unknown as string)
        : undefined,
      to: filters.dateRange.to
        ? new Date(filters.dateRange.to as unknown as string)
        : undefined
    }
  }
}

function migrateRetiredDateRange(
  filters: DashboardFilterState,
  defaults: DashboardFilterState
): DashboardFilterState {
  if (filters?.dateRangeLabel !== RETIRED_TODAY_LABEL) return filters
  return {
    ...filters,
    dateRange: defaults.dateRange,
    dateRangeLabel: defaults.dateRangeLabel
  }
}

export const DEFAULT_DATE_RANGE_LABEL = DATE_RANGE_LABELS.OLDER_THAN_7_DAYS

function buildDefaultFilters(
  tileSlug: string,
  activatedAt?: ActivationDate
): DashboardFilterState {
  return {
    dateRange: buildOlderThan7DaysRange(activatedAt),
    dateRangeLabel: DEFAULT_DATE_RANGE_LABEL,
    location: null,
    providers: [],
    patient: null,
    tileSlug
  }
}

export const DEFAULT_DASHBOARD_FILTERS = buildDefaultFilters('incomplete-op-reports')

interface DashboardFilterContextValue {
  filters: DashboardFilterState
  setFilters: (filters: DashboardFilterState) => void
}

const DashboardFilterContext = createContext<DashboardFilterContextValue>({
  filters: DEFAULT_DASHBOARD_FILTERS,
  setFilters: () => {}
})

export function DashboardFilterProvider({
  children
}: {
  children: React.ReactNode
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { currentAccount } = useAppContext() as any
  const isProvider = currentAccount?.type === accountType.PROVIDER

  const storageKey = isProvider ? STORAGE_KEY_PROVIDER : STORAGE_KEY_STAFF
  const activatedAt = currentAccount?.organization?.createdAt
  const defaultFilters = useMemo(
    () =>
      buildDefaultFilters(
        isProvider ? 'not-started-op-reports' : 'incomplete-op-reports',
        activatedAt
      ),
    [isProvider, activatedAt]
  )

  const [rawFilters, setFilters] = usePersistedState(
    storageKey,
    defaultFilters
  ) as any

  const contextValue = useMemo(
    () => ({
      filters: hydrateFilters(
        migrateRetiredDateRange(rawFilters, defaultFilters)
      ) as DashboardFilterState,
      setFilters
    }),
    [rawFilters, setFilters, defaultFilters]
  )

  return (
    <DashboardFilterContext.Provider value={contextValue}>
      {children}
    </DashboardFilterContext.Provider>
  )
}

export function useDashboardFilterContext() {
  return useContext(DashboardFilterContext)
}
